import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Filter as FunnelIcon, Lock, X as CloseIcon, Download } from 'lucide-react';
import { apiClient } from '@/lib/apiClient';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';

interface FunnelStage {
  stageId: string | null;
  stageKey: string;
  name: string;
  order: number;
  isGate: boolean;
  count: number;
  moneyTotal: number;
  conversionToNext: number | null;
  helpText: string | null;
}

interface FunnelSnapshot {
  asOf: string;
  window: string;
  templateId: string | null;
  stages: FunnelStage[];
}

interface DrillDownRow {
  kind: string;
  id: string;
  label: string;
  money: number;
  secondary: string;
}

interface DimensionSpec {
  id: string;
  name: string;
  label: string;
  kind: 'ENUM' | 'RANGE' | 'NAMED_LIST';
  sourceType: 'DERIVED' | 'CUSTOM_FIELD' | 'MANUAL';
  sourceRef: string | null;
  values: {
    values?: Array<{ key: string; label: string }>;
    bands?: Array<{ key: string; label: string; min?: number; max?: number }>;
    lists?: Array<{ key: string; label: string; entity_ids?: string[] }>;
  };
  isActive: boolean;
  displayOrder: number;
}

function dimensionValueKeys(d: DimensionSpec): Array<{ key: string; label: string }> {
  if (d.kind === 'ENUM') return d.values.values ?? [];
  if (d.kind === 'RANGE')
    return (d.values.bands ?? []).map((b) => ({ key: b.key, label: b.label }));
  if (d.kind === 'NAMED_LIST')
    return (d.values.lists ?? []).map((l) => ({ key: l.key, label: l.label }));
  return [];
}

const WINDOW_CHOICES = [
  { code: 'P1W', label: '1w' },
  { code: 'P1M', label: '1m' },
  { code: 'P1Q', label: '1q' },
  { code: 'P1Y', label: '1y' },
] as const;

type WidthMode = 'money' | 'count';

const STAGE_PALETTE = [
  '#3b82f6', // blue-500 — top of funnel
  '#6366f1', // indigo-500
  '#8b5cf6', // violet-500
  '#a855f7', // purple-500
  '#d946ef', // fuchsia-500
  '#ec4899', // pink-500
  '#f43f5e', // rose-500
  '#10b981', // emerald-500 — won
  '#ef4444', // red-500 — lost (last)
];

function fmtMoney(n: number): string {
  if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `$${(n / 1_000).toFixed(0)}K`;
  return `$${n.toFixed(0)}`;
}

function fmtPct(n: number): string {
  return `${(n * 100).toFixed(0)}%`;
}

function todayIso(): string {
  return new Date().toISOString().slice(0, 10);
}

/**
 * Builds the query-param object the /analytics/funnel endpoint expects,
 * including the segmentation `dim=<id>:<v1>,<v2>` repeated params per
 * 59.9. Filters with no selection are simply omitted.
 *
 * Returns axios's `params` shape — values can be string or string[];
 * axios serialises arrays as repeated query params by default which is
 * exactly what the backend's @RequestParam(name="dim") List<String>
 * needs.
 */
function buildFunnelParams(input: {
  asOf: string;
  window: string;
  teamRootId: string;
  industries: string[];
  sizeBuckets: string[];
  selectedByDim: Record<string, string[]>;
}): Record<string, string | string[]> {
  const params: Record<string, string | string[]> = {
    asOf: input.asOf,
    window: input.window,
  };
  if (input.teamRootId) params.teamRootId = input.teamRootId;
  if (input.industries.length > 0) params.industry = input.industries.join(',');
  if (input.sizeBuckets.length > 0) params.sizeBucket = input.sizeBuckets.join(',');
  const dimEntries = Object.entries(input.selectedByDim)
    .filter(([, v]) => v.length > 0)
    .map(([dimId, values]) => `${dimId}:${values.join(',')}`);
  if (dimEntries.length > 0) params.dim = dimEntries;
  return params;
}

/**
 * Story 85.3 — Funnel Explorer page. Hero SVG funnel as the centerpiece;
 * sliders for as-of date and window; filters panel on the right.
 *
 * The visual: one polygon per stage, joined edge-to-edge so each
 * trapezoid's top width = previous stage's bottom width. Width scales
 * to either count or money (toggle, default money). Stage label
 * centered inside the polygon; KPIs floating right with the
 * conversion-to-next % chip.
 *
 * Polish (gradients, animations, responsive) is story 85.4.
 */
export function FunnelExplorerPage() {
  const { t } = useTranslation();

  // Sliders / config state.
  const [asOf, setAsOf] = useState<string>(() => {
    if (typeof window !== 'undefined') {
      return window.localStorage.getItem('funnel.asOf') ?? todayIso();
    }
    return todayIso();
  });
  const [windowCode, setWindowCode] = useState<string>(() => {
    if (typeof window !== 'undefined') {
      return window.localStorage.getItem('funnel.window') ?? 'P1W';
    }
    return 'P1W';
  });

  // Story 1.6 — explicit date-from filter. Empty string = use windowCode
  // (legacy behaviour). When set, asOf becomes the right edge and dateFrom
  // the left edge; the funnel volume is computed for that explicit range.
  const [dateFrom, setDateFrom] = useState<string>(() => {
    if (typeof window !== 'undefined') {
      return window.localStorage.getItem('funnel.dateFrom') ?? '';
    }
    return '';
  });

  // Story 1.6 — auto-refresh polling. Off by default; user enables for
  // a live-monitoring view. Interval in seconds.
  const [autoRefresh, setAutoRefresh] = useState<boolean>(() => {
    if (typeof window !== 'undefined') {
      return window.localStorage.getItem('funnel.autoRefresh') === 'true';
    }
    return false;
  });
  const [refreshIntervalSec, setRefreshIntervalSec] = useState<number>(() => {
    if (typeof window !== 'undefined') {
      const raw = window.localStorage.getItem('funnel.refreshIntervalSec');
      const parsed = raw ? parseInt(raw, 10) : NaN;
      return Number.isFinite(parsed) && parsed >= 30 ? parsed : 60;
    }
    return 60;
  });
  const [refreshTick, setRefreshTick] = useState(0);
  const [widthMode, setWidthMode] = useState<WidthMode>('money');

  const [snapshot, setSnapshot] = useState<FunnelSnapshot | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Story 85.7 — comparison mode. compareAsOf defaults to 7 days
  // before the primary asOf so opening Compare gives an immediately
  // useful side-by-side delta.
  const [compareEnabled, setCompareEnabled] = useState(false);
  const [compareAsOf, setCompareAsOf] = useState<string>('');
  const [compareSnapshot, setCompareSnapshot] = useState<FunnelSnapshot | null>(null);

  // Story 85.6 — drill-down side panel.
  const [drillStage, setDrillStage] = useState<FunnelStage | null>(null);
  const [drillRows, setDrillRows] = useState<DrillDownRow[]>([]);
  const [drillLoading, setDrillLoading] = useState(false);

  // Story 85.5 — filters.
  const [teamRootId, setTeamRootId] = useState<string>(() => {
    if (typeof window !== 'undefined') {
      return window.localStorage.getItem('funnel.teamRootId') ?? '';
    }
    return '';
  });
  const [industries, setIndustries] = useState<string[]>(() => {
    if (typeof window !== 'undefined') {
      const raw = window.localStorage.getItem('funnel.industries');
      return raw ? raw.split(',').filter(Boolean) : [];
    }
    return [];
  });
  const [sizeBuckets, setSizeBuckets] = useState<string[]>(() => {
    if (typeof window !== 'undefined') {
      const raw = window.localStorage.getItem('funnel.sizeBuckets');
      return raw ? raw.split(',').filter(Boolean) : [];
    }
    return [];
  });

  const [orgUsers, setOrgUsers] = useState<Array<{ id: string; email: string; role: string }>>([]);
  const [allIndustries, setAllIndustries] = useState<string[]>([]);
  const [allSizeBuckets] = useState<string[]>(['SMALL', 'MEDIUM', 'LARGE', 'ENTERPRISE']);

  // Story 59.9 — segmentation dimensions auto-discovered from the
  // tenant's /segmentation/dimensions config. Each active dimension
  // renders as its own MultiSelectChips group, replacing the legacy
  // industry + size_bucket hardcoded chips when any dimensions exist.
  const [dimensions, setDimensions] = useState<DimensionSpec[]>([]);
  const [selectedByDim, setSelectedByDim] = useState<Record<string, string[]>>(() => {
    if (typeof window !== 'undefined') {
      const raw = window.localStorage.getItem('funnel.dimensions');
      if (raw) {
        try {
          return JSON.parse(raw) as Record<string, string[]>;
        } catch {
          return {};
        }
      }
    }
    return {};
  });

  // Load filter option data once.
  useEffect(() => {
    apiClient
      .get<Array<{ id: string; email: string; role: string }>>('/users')
      .then(({ data }) => setOrgUsers(data))
      .catch(() => setOrgUsers([]));
    apiClient
      .get<Array<{ industry: string | null }>>('/accounts')
      .then(({ data }) => {
        const distinct = Array.from(
          new Set(data.map((a) => a.industry).filter((x): x is string => Boolean(x))),
        ).sort();
        setAllIndustries(distinct);
      })
      .catch(() => setAllIndustries([]));
    apiClient
      .get<DimensionSpec[]>('/segmentation/dimensions')
      .then(({ data }) => setDimensions(data))
      .catch(() => setDimensions([]));
  }, []);

  useEffect(() => {
    if (typeof window !== 'undefined') {
      window.localStorage.setItem('funnel.teamRootId', teamRootId);
      window.localStorage.setItem('funnel.industries', industries.join(','));
      window.localStorage.setItem('funnel.sizeBuckets', sizeBuckets.join(','));
      window.localStorage.setItem('funnel.dimensions', JSON.stringify(selectedByDim));
    }
  }, [teamRootId, industries, sizeBuckets, selectedByDim]);

  useEffect(() => {
    if (typeof window !== 'undefined') {
      window.localStorage.setItem('funnel.asOf', asOf);
      window.localStorage.setItem('funnel.window', windowCode);
      window.localStorage.setItem('funnel.dateFrom', dateFrom);
      window.localStorage.setItem('funnel.autoRefresh', String(autoRefresh));
      window.localStorage.setItem('funnel.refreshIntervalSec', String(refreshIntervalSec));
    }
  }, [asOf, windowCode, dateFrom, autoRefresh, refreshIntervalSec]);

  // Story 1.6 — auto-refresh tick. When enabled, increment refreshTick
  // every interval, which is in the fetch effect's deps so the funnel
  // re-fetches without changing user-visible state.
  useEffect(() => {
    if (!autoRefresh) return;
    const handle = setInterval(
      () => setRefreshTick((n) => n + 1),
      Math.max(30, refreshIntervalSec) * 1000,
    );
    return () => clearInterval(handle);
  }, [autoRefresh, refreshIntervalSec]);

  // Debounced fetch — sliders should not hammer the endpoint while dragging.
  // refreshTick (story 1.6) is in deps so auto-refresh triggers a re-fetch
  // without disturbing other user-visible state.
  useEffect(() => {
    setLoading(true);
    const handle = setTimeout(() => {
      const asOfIso = new Date(`${asOf}T23:59:59Z`).toISOString();
      const params = buildFunnelParams({
        asOf: asOfIso,
        window: windowCode,
        teamRootId,
        industries,
        sizeBuckets,
        selectedByDim,
      });
      // Story 1.6 — when explicit dateFrom is set, pass it as
      // start-of-day UTC and let the backend treat the range as
      // [dateFrom, asOf]. Backwards compatible: empty dateFrom keeps
      // the windowCode-driven legacy behaviour.
      if (dateFrom) {
        params.dateFrom = new Date(`${dateFrom}T00:00:00Z`).toISOString();
      }
      apiClient
        .get<FunnelSnapshot>('/analytics/funnel', { params })
        .then(({ data }) => {
          setSnapshot(data);
          setError(null);
        })
        .catch(() => setError(t('funnel.error_load')))
        .finally(() => setLoading(false));
    }, 200);
    return () => clearTimeout(handle);
  }, [asOf, windowCode, dateFrom, teamRootId, industries, sizeBuckets, selectedByDim, refreshTick, t]);

  // Story 85.7 — fetch the compare snapshot in parallel when enabled.
  // Same filters apply so deltas stay apples-to-apples.
  useEffect(() => {
    if (!compareEnabled || !compareAsOf) {
      setCompareSnapshot(null);
      return;
    }
    const handle = setTimeout(() => {
      const asOfIso = new Date(`${compareAsOf}T23:59:59Z`).toISOString();
      const params = buildFunnelParams({
        asOf: asOfIso,
        window: windowCode,
        teamRootId,
        industries,
        sizeBuckets,
        selectedByDim,
      });
      apiClient
        .get<FunnelSnapshot>('/analytics/funnel', { params })
        .then(({ data }) => setCompareSnapshot(data))
        .catch(() => setCompareSnapshot(null));
    }, 200);
    return () => clearTimeout(handle);
  }, [compareEnabled, compareAsOf, windowCode, teamRootId, industries, sizeBuckets, selectedByDim]);

  function toggleCompare() {
    if (compareEnabled) {
      setCompareEnabled(false);
      setCompareSnapshot(null);
      return;
    }
    // Default compareAsOf to 7 days before primary so the deltas are
    // immediately useful — most managers want week-over-week first.
    if (!compareAsOf) {
      const d = new Date(`${asOf}T00:00:00Z`);
      d.setUTCDate(d.getUTCDate() - 7);
      setCompareAsOf(d.toISOString().slice(0, 10));
    }
    setCompareEnabled(true);
  }

  return (
    <div className="space-y-6 p-6">
      <header className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <FunnelIcon className="h-5 w-5 text-muted-foreground" />
          <div>
            <h1 className="text-2xl font-semibold">{t('funnel.title')}</h1>
            <p className="text-sm text-muted-foreground">{t('funnel.subtitle')}</p>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <div className="flex items-center gap-1 rounded-md border bg-muted p-0.5 text-xs">
            {(['money', 'count'] as WidthMode[]).map((m) => (
              <button
                key={m}
                type="button"
                onClick={() => setWidthMode(m)}
                className={`rounded px-3 py-1 transition ${
                  widthMode === m ? 'bg-card font-medium shadow-sm' : 'text-muted-foreground'
                }`}
              >
                {t(`funnel.width_${m}`)}
              </button>
            ))}
          </div>
          <Button
            size="sm"
            variant={compareEnabled ? 'default' : 'outline'}
            onClick={toggleCompare}
          >
            {compareEnabled ? t('funnel.compare_off') : t('funnel.compare_on')}
          </Button>
          {compareEnabled && (
            <input
              type="date"
              value={compareAsOf}
              max={asOf}
              onChange={(e) => setCompareAsOf(e.target.value)}
              className="rounded-md border bg-transparent px-2 py-1 text-xs"
              aria-label={t('funnel.compare_as_of') ?? 'Compare to'}
            />
          )}
          <Button
            size="sm"
            variant="outline"
            onClick={() => snapshot && exportCsv(snapshot, asOf)}
            disabled={!snapshot}
          >
            <Download className="mr-1 h-3.5 w-3.5" />
            CSV
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={() => exportSvgPng(asOf)}
            disabled={!snapshot}
          >
            <Download className="mr-1 h-3.5 w-3.5" />
            PNG
          </Button>
        </div>
      </header>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-[2fr_1fr]">
        {/* Funnel hero */}
        <Card className="min-h-[480px]">
          {error ? (
            <p className="text-sm text-destructive">{error}</p>
          ) : loading && !snapshot ? (
            <p className="text-sm text-muted-foreground">{t('common.loading')}</p>
          ) : !snapshot || snapshot.stages.length === 0 ? (
            <p className="text-sm text-muted-foreground">{t('funnel.empty')}</p>
          ) : (
            <FunnelChart
              snapshot={snapshot}
              widthMode={widthMode}
              compareSnapshot={compareEnabled ? compareSnapshot : null}
              onStageClick={(stage) => {
                setDrillStage(stage);
                setDrillLoading(true);
                const asOfIso = new Date(`${asOf}T23:59:59Z`).toISOString();
                const params = buildFunnelParams({
                  asOf: asOfIso,
                  window: windowCode,
                  teamRootId,
                  industries,
                  sizeBuckets,
                  selectedByDim,
                });
                params.stageKey = stage.stageKey;
                apiClient
                  .get<DrillDownRow[]>('/analytics/funnel/drilldown', { params })
                  .then(({ data }) => setDrillRows(data))
                  .catch(() => setDrillRows([]))
                  .finally(() => setDrillLoading(false));
              }}
            />
          )}
        </Card>

        {/* Filters + sliders panel */}
        <div className="space-y-4">
          <Card className="space-y-3">
            <h2 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
              {t('funnel.controls_title')}
            </h2>
            <div className="space-y-1">
              <label className="text-xs font-medium">{t('funnel.as_of')}</label>
              <input
                type="date"
                value={asOf}
                onChange={(e) => setAsOf(e.target.value)}
                className="w-full rounded-md border bg-transparent px-3 py-2 text-sm"
              />
            </div>
            <div className="space-y-1">
              <label className="text-xs font-medium">{t('funnel.date_from_label')}</label>
              <div className="flex items-center gap-2">
                <input
                  type="date"
                  value={dateFrom}
                  max={asOf}
                  onChange={(e) => setDateFrom(e.target.value)}
                  className="flex-1 rounded-md border bg-transparent px-3 py-2 text-sm"
                />
                {dateFrom && (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setDateFrom('')}
                  >
                    {t('funnel.date_from_clear')}
                  </Button>
                )}
              </div>
              <p className="text-[10px] text-muted-foreground">
                {dateFrom
                  ? t('funnel.date_from_active', { from: dateFrom, to: asOf })
                  : t('funnel.date_from_help')}
              </p>
            </div>
            <div className="space-y-1">
              <label className="text-xs font-medium">{t('funnel.window')}</label>
              <div className="flex gap-1">
                {WINDOW_CHOICES.map((w) => (
                  <Button
                    key={w.code}
                    size="sm"
                    variant={windowCode === w.code ? 'default' : 'outline'}
                    onClick={() => setWindowCode(w.code)}
                    className="flex-1"
                  >
                    {w.label}
                  </Button>
                ))}
              </div>
            </div>
            <div className="space-y-1 border-t pt-3">
              <label className="flex items-center gap-2 text-xs font-medium">
                <input
                  type="checkbox"
                  checked={autoRefresh}
                  onChange={(e) => setAutoRefresh(e.target.checked)}
                  className="h-3.5 w-3.5"
                />
                {t('funnel.auto_refresh_label')}
              </label>
              {autoRefresh && (
                <div className="flex items-center gap-2 pl-5">
                  <span className="text-[10px] text-muted-foreground">
                    {t('funnel.auto_refresh_every')}
                  </span>
                  <select
                    value={refreshIntervalSec}
                    onChange={(e) => setRefreshIntervalSec(parseInt(e.target.value, 10) || 60)}
                    className="rounded border bg-transparent px-2 py-0.5 text-xs"
                  >
                    {[30, 60, 120, 300, 600].map((s) => (
                      <option key={s} value={s}>
                        {s < 60 ? `${s}s` : `${Math.floor(s / 60)}m`}
                      </option>
                    ))}
                  </select>
                </div>
              )}
            </div>
          </Card>

          <Card className="space-y-3">
            <h2 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
              {t('funnel.filters_title')}
            </h2>
            <div className="space-y-1">
              <label className="text-xs font-medium">{t('funnel.team_root')}</label>
              <select
                value={teamRootId}
                onChange={(e) => setTeamRootId(e.target.value)}
                className="w-full rounded-md border bg-transparent px-3 py-2 text-sm"
              >
                <option value="">{t('funnel.team_all')}</option>
                {orgUsers.map((u) => (
                  <option key={u.id} value={u.id}>
                    {u.email} ({u.role})
                  </option>
                ))}
              </select>
            </div>
            {/*
             * Story 59.9 — when the tenant has configured segmentation
             * dimensions, render one chip group per dimension. When they
             * have none, fall back to the legacy industry / size_bucket
             * chips so existing tenants keep their filter UX. The
             * deprecation of the legacy params is tracked under 59.10.
             */}
            {dimensions.length > 0 ? (
              dimensions.map((dim) => {
                const opts = dimensionValueKeys(dim);
                if (opts.length === 0) return null;
                const optMap = new Map(opts.map((o) => [o.key, o.label]));
                return (
                  <div key={dim.id} className="space-y-1">
                    <label className="text-xs font-medium">{dim.label}</label>
                    <MultiSelectChips
                      options={opts.map((o) => o.key)}
                      labelFor={(k) => optMap.get(k) ?? k}
                      value={selectedByDim[dim.id] ?? []}
                      onChange={(next) =>
                        setSelectedByDim((prev) => ({ ...prev, [dim.id]: next }))
                      }
                    />
                  </div>
                );
              })
            ) : (
              <>
                <div className="space-y-1">
                  <label className="text-xs font-medium">{t('funnel.industry')}</label>
                  <MultiSelectChips
                    options={allIndustries}
                    value={industries}
                    onChange={setIndustries}
                    emptyLabel={t('funnel.no_industries')}
                  />
                </div>
                <div className="space-y-1">
                  <label className="text-xs font-medium">{t('funnel.size_bucket')}</label>
                  <MultiSelectChips
                    options={allSizeBuckets}
                    value={sizeBuckets}
                    onChange={setSizeBuckets}
                  />
                </div>
              </>
            )}
            {(teamRootId ||
              industries.length > 0 ||
              sizeBuckets.length > 0 ||
              Object.values(selectedByDim).some((v) => v.length > 0)) && (
              <Button
                size="sm"
                variant="ghost"
                onClick={() => {
                  setTeamRootId('');
                  setIndustries([]);
                  setSizeBuckets([]);
                  setSelectedByDim({});
                }}
              >
                {t('funnel.clear_filters')}
              </Button>
            )}
          </Card>
        </div>
      </div>

      {/* SVG element gets a stable id so the PNG export can locate it
          without prop-drilling a ref through FunnelChart. */}

      {drillStage && (
        <DrillDownPanel
          stage={drillStage}
          rows={drillRows}
          loading={drillLoading}
          onClose={() => {
            setDrillStage(null);
            setDrillRows([]);
          }}
        />
      )}
    </div>
  );
}

function DrillDownPanel({
  stage,
  rows,
  loading,
  onClose,
}: {
  stage: FunnelStage;
  rows: DrillDownRow[];
  loading: boolean;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  return (
    <div className="fixed inset-0 z-40 flex justify-end">
      <button
        type="button"
        aria-label="Close"
        onClick={onClose}
        className="flex-1 bg-black/30"
      />
      <div className="w-full max-w-md overflow-y-auto bg-card p-6 shadow-xl">
        <div className="mb-4 flex items-center justify-between">
          <div>
            <h2 className="text-lg font-semibold">{stage.name}</h2>
            <p className="text-xs text-muted-foreground">
              {stage.count.toLocaleString()} ·{' '}
              {Number(stage.moneyTotal).toLocaleString('en-US', {
                style: 'currency',
                currency: 'USD',
                maximumFractionDigits: 0,
              })}
            </p>
          </div>
          <Button size="sm" variant="ghost" onClick={onClose}>
            <CloseIcon className="h-4 w-4" />
          </Button>
        </div>
        {loading ? (
          <p className="text-sm text-muted-foreground">{t('common.loading')}</p>
        ) : rows.length === 0 ? (
          <p className="text-sm text-muted-foreground">{t('funnel.drill_empty')}</p>
        ) : (
          <ul className="space-y-2">
            {rows.map((row) => (
              <li key={row.id}>
                <Link
                  to={
                    row.kind === 'OPPORTUNITY'
                      ? `/opportunities/${row.id}`
                      : `/leads/${row.id}`
                  }
                  className="flex items-center justify-between rounded-md border bg-card px-3 py-2 text-sm hover:bg-muted"
                >
                  <div className="min-w-0">
                    <div className="truncate font-medium">{row.label}</div>
                    <div className="text-xs text-muted-foreground">{row.secondary}</div>
                  </div>
                  {row.money > 0 && (
                    <span className="text-xs font-mono">
                      {Number(row.money).toLocaleString('en-US', {
                        style: 'currency',
                        currency: 'USD',
                        maximumFractionDigits: 0,
                      })}
                    </span>
                  )}
                </Link>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

function FunnelChart({
  snapshot,
  widthMode,
  onStageClick,
  compareSnapshot,
}: {
  snapshot: FunnelSnapshot;
  widthMode: WidthMode;
  onStageClick?: (stage: FunnelStage) => void;
  compareSnapshot?: FunnelSnapshot | null;
}) {
  const { stages } = snapshot;
  const values = stages.map((s) => (widthMode === 'money' ? Number(s.moneyTotal) : s.count));
  const maxValue = useMemo(() => Math.max(1, ...values), [values]);
  const [hoverIdx, setHoverIdx] = useState<number | null>(null);

  const width = 720;
  const stageHeight = 64;
  const gap = 4;
  const totalHeight = stages.length * stageHeight + (stages.length - 1) * gap;
  const minWidthPx = 60;

  function widthForValue(v: number): number {
    return Math.max(minWidthPx, (v / maxValue) * width);
  }

  // Lighten a hex by mixing with white. Used for the linear gradient
  // top stop so each polygon reads as a soft 3D shape rather than flat.
  function lighten(hex: string, amount: number): string {
    const v = hex.replace('#', '');
    const r = parseInt(v.slice(0, 2), 16);
    const g = parseInt(v.slice(2, 4), 16);
    const b = parseInt(v.slice(4, 6), 16);
    const lr = Math.round(r + (255 - r) * amount);
    const lg = Math.round(g + (255 - g) * amount);
    const lb = Math.round(b + (255 - b) * amount);
    return `#${[lr, lg, lb].map((c) => c.toString(16).padStart(2, '0')).join('')}`;
  }

  // 85.7 — paired stage from the comparison snapshot, looked up by
  // stageKey. Stage ordering between snapshots can differ if the
  // template changed mid-window, but stageKey is stable.
  const compareByKey = useMemo(() => {
    const map = new Map<string, FunnelStage>();
    if (compareSnapshot) {
      for (const s of compareSnapshot.stages) map.set(s.stageKey, s);
    }
    return map;
  }, [compareSnapshot]);

  return (
    <div className="flex flex-col items-start gap-6 md:flex-row">
      <svg
        id="kairos-funnel-svg"
        viewBox={`0 0 ${width} ${totalHeight}`}
        className="w-full max-w-2xl"
        style={{ height: totalHeight }}
      >
        <defs>
          <filter id="kairos-funnel-shadow" x="-5%" y="-5%" width="110%" height="120%">
            <feDropShadow dx="0" dy="2" stdDeviation="3" floodOpacity="0.15" />
          </filter>
          {stages.map((_, i) => {
            const base = STAGE_PALETTE[Math.min(i, STAGE_PALETTE.length - 1)];
            return (
              <linearGradient
                key={i}
                id={`kairos-funnel-grad-${i}`}
                x1="0%"
                y1="0%"
                x2="0%"
                y2="100%"
              >
                <stop offset="0%" stopColor={lighten(base, 0.25)} />
                <stop offset="100%" stopColor={base} />
              </linearGradient>
            );
          })}
        </defs>
        {stages.map((stage, i) => {
          const value = widthMode === 'money' ? Number(stage.moneyTotal) : stage.count;
          const next = stages[i + 1];
          const nextValue = next ? (widthMode === 'money' ? Number(next.moneyTotal) : next.count) : value;
          const wTop = widthForValue(value);
          const wBottom = widthForValue(nextValue);
          const y = i * (stageHeight + gap);
          const cx = width / 2;
          const points = [
            `${cx - wTop / 2},${y}`,
            `${cx + wTop / 2},${y}`,
            `${cx + wBottom / 2},${y + stageHeight}`,
            `${cx - wBottom / 2},${y + stageHeight}`,
          ].join(' ');
          const isHovered = hoverIdx === i;
          const baseOpacity = value > 0 ? 0.92 : 0.4;
          return (
            <g
              key={stage.stageKey}
              style={{
                cursor: onStageClick ? 'pointer' : 'default',
                transition: 'opacity 200ms ease-out, transform 200ms ease-out',
                opacity: hoverIdx === null || isHovered ? 1 : 0.6,
                transformOrigin: `${cx}px ${y + stageHeight / 2}px`,
                transform: isHovered ? 'scale(1.015)' : 'scale(1)',
              }}
              onMouseEnter={() => setHoverIdx(i)}
              onMouseLeave={() => setHoverIdx(null)}
              onClick={() => onStageClick?.(stage)}
            >
              {stage.helpText && <title>{stage.helpText}</title>}
              <polygon
                points={points}
                fill={`url(#kairos-funnel-grad-${i})`}
                opacity={baseOpacity}
                stroke={stage.isGate ? '#1f2937' : isHovered ? '#0f172a' : 'none'}
                strokeWidth={stage.isGate ? 2 : isHovered ? 1.5 : 0}
                strokeDasharray={stage.isGate ? '6 3' : undefined}
                filter="url(#kairos-funnel-shadow)"
                style={{ transition: 'opacity 200ms ease-out' }}
              />
              <text
                x={cx}
                y={y + stageHeight / 2 + 5}
                textAnchor="middle"
                fontSize="14"
                fontWeight="600"
                fill="#ffffff"
                style={{
                  textShadow: '0 1px 3px rgba(0,0,0,0.4)',
                  pointerEvents: 'none',
                  letterSpacing: '0.01em',
                }}
              >
                {stage.name}
                {stage.isGate ? ' 🔒' : ''}
              </text>
            </g>
          );
        })}
      </svg>

      {/* KPI rail to the right of the funnel */}
      <ol className="flex-1 space-y-2 self-stretch" style={{ minHeight: totalHeight }}>
        {stages.map((stage, i) => {
          const isHovered = hoverIdx === i;
          const cmp = compareByKey.get(stage.stageKey);
          return (
          <li
            key={stage.stageKey}
            onMouseEnter={() => setHoverIdx(i)}
            onMouseLeave={() => setHoverIdx(null)}
            className={`flex items-center justify-between rounded-md border px-3 py-2 transition-all duration-200 ${
              isHovered ? 'bg-muted/60 shadow-sm' : 'bg-card'
            }`}
            style={{ minHeight: stageHeight }}
          >
            <div className="flex min-w-0 items-center gap-2">
              <span
                aria-hidden
                className="h-3 w-3 shrink-0 rounded-sm"
                style={{ backgroundColor: STAGE_PALETTE[Math.min(i, STAGE_PALETTE.length - 1)] }}
              />
              <div className="min-w-0">
                <div className="flex items-center gap-1 truncate text-sm font-medium">
                  {stage.name}
                  {stage.isGate && <Lock className="h-3 w-3 text-muted-foreground" />}
                  {stage.helpText && (
                    <span title={stage.helpText} className="cursor-help text-muted-foreground">
                      <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                        <circle cx="12" cy="12" r="10"/><path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"/><line x1="12" y1="17" x2="12.01" y2="17"/>
                      </svg>
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-2 text-xs text-muted-foreground">
                  <span>{stage.count.toLocaleString()} · {fmtMoney(Number(stage.moneyTotal))}</span>
                  {cmp && <DeltaBadge stage={stage} compare={cmp} />}
                </div>
              </div>
            </div>
            {stage.conversionToNext !== null && i < stages.length - 1 && (
              <span
                className={`shrink-0 rounded-full px-2 py-0.5 text-xs font-semibold ${
                  Number(stage.conversionToNext) >= 0.5
                    ? 'bg-emerald-900/40 text-emerald-200'
                    : Number(stage.conversionToNext) >= 0.2
                      ? 'bg-amber-900/40 text-amber-200'
                      : 'bg-rose-900/40 text-rose-200'
                }`}
              >
                ↓ {fmtPct(Number(stage.conversionToNext))}
              </span>
            )}
          </li>
          );
        })}
      </ol>
    </div>
  );
}

/**
 * Story 85.7 — delta badge comparing the primary stage against the
 * compare snapshot for the same stage. Shows the count and money
 * delta with a sign + colour cue (green for growth, rose for shrink).
 */
function DeltaBadge({ stage, compare }: { stage: FunnelStage; compare: FunnelStage }) {
  const dCount = stage.count - compare.count;
  const dMoney = Number(stage.moneyTotal) - Number(compare.moneyTotal);
  if (dCount === 0 && Math.abs(dMoney) < 1) return null;
  const positive = dCount > 0 || dMoney > 0;
  const sign = positive ? '+' : '';
  return (
    <span
      className={`rounded-full px-1.5 py-0.5 text-[10px] font-semibold ${
        positive ? 'bg-emerald-900/40 text-emerald-200' : 'bg-rose-900/40 text-rose-200'
      }`}
      title={`vs compare: count ${sign}${dCount}, money ${sign}${fmtMoney(dMoney)}`}
    >
      {sign}{dCount} · {sign}{fmtMoney(dMoney)}
    </span>
  );
}

function exportCsv(snapshot: FunnelSnapshot, asOf: string): void {
  const headers = ['stage_order', 'name', 'count', 'money_total', 'conversion_to_next', 'is_gate'];
  const rows = snapshot.stages.map((s) =>
    [
      s.order,
      JSON.stringify(s.name), // quoted for safety with commas
      s.count,
      Number(s.moneyTotal).toFixed(2),
      s.conversionToNext === null ? '' : Number(s.conversionToNext).toFixed(4),
      s.isGate ? 'true' : 'false',
    ].join(','),
  );
  const csv = [headers.join(','), ...rows].join('\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `funnel-${asOf}.csv`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  setTimeout(() => URL.revokeObjectURL(url), 100);
}

function exportSvgPng(asOf: string): void {
  const svgEl = document.getElementById('kairos-funnel-svg') as SVGSVGElement | null;
  if (!svgEl) return;
  const serializer = new XMLSerializer();
  const svgString = serializer.serializeToString(svgEl);
  const svgBlob = new Blob([svgString], { type: 'image/svg+xml;charset=utf-8' });
  const url = URL.createObjectURL(svgBlob);
  const img = new Image();
  img.onload = () => {
    const canvas = document.createElement('canvas');
    const scale = 2; // 2x for retina
    canvas.width = (svgEl.viewBox.baseVal.width || svgEl.clientWidth) * scale;
    canvas.height = (svgEl.viewBox.baseVal.height || svgEl.clientHeight) * scale;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
    canvas.toBlob((blob) => {
      if (!blob) return;
      const pngUrl = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = pngUrl;
      link.download = `funnel-${asOf}.png`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      setTimeout(() => URL.revokeObjectURL(pngUrl), 100);
    }, 'image/png');
    URL.revokeObjectURL(url);
  };
  img.src = url;
}

function MultiSelectChips({
  options,
  value,
  onChange,
  emptyLabel,
  labelFor,
}: {
  options: string[];
  value: string[];
  onChange: (next: string[]) => void;
  emptyLabel?: string;
  labelFor?: (key: string) => string;
}) {
  if (options.length === 0) {
    return <p className="text-xs text-muted-foreground">{emptyLabel ?? '—'}</p>;
  }
  return (
    <div className="flex flex-wrap gap-1">
      {options.map((opt) => {
        const selected = value.includes(opt);
        return (
          <button
            key={opt}
            type="button"
            onClick={() => {
              if (selected) onChange(value.filter((v) => v !== opt));
              else onChange([...value, opt]);
            }}
            className={`rounded-full px-2 py-0.5 text-xs transition ${
              selected
                ? 'bg-primary text-primary-foreground'
                : 'border bg-card text-muted-foreground hover:bg-muted'
            }`}
          >
            {labelFor ? labelFor(opt) : opt}
          </button>
        );
      })}
    </div>
  );
}
